import { errorMessage, isNetworkError } from '../api/client'
import { api } from '../api/endpoints'
import type { DeviceInfo, DeviceStatus } from '../api/types'
import { setCachedMe } from '../db/meta'
import { deviceAllowed, useDeviceGate } from '../state/device'
import { useSession } from '../state/session'
import { ensureDeviceAttested, type AttestOptions } from './attest'
import { getOrCreateDeviceKey } from './deviceKey'

/**
 * Device gate controller: runs attestation after GET /api/me, keeps the gate state in sync with the
 * server while a device waits for approval (poll every 15 s and on visibilitychange), and tells the
 * lifecycle when the app may start or must stop realtime.
 */

export const DEVICE_POLL_MS = 15_000
const MIN_RECHECK_GAP_MS = 5_000

export interface DeviceGateHandlers {
  /** The device became allowed after being gated: continue the normal start-up. */
  onAllowed: () => void
  /** The device just became gated: stop realtime and sync. */
  onGated: () => void
}

let handlers: DeviceGateHandlers = { onAllowed: () => {}, onGated: () => {} }
let gated = false
let pollTimer: ReturnType<typeof setInterval> | null = null
let inFlight: Promise<void> | null = null
let lastReportCheck = 0

export function configureDeviceGate(next: DeviceGateHandlers): void {
  handlers = next
}

/** Store the device everywhere it is read: the gate store, the session and the cached Me. */
export function applyDevice(device: DeviceInfo): void {
  useDeviceGate.getState().setDevice(device)
  const session = useSession.getState()
  if (session.me) {
    const next = { ...session.me, device }
    session.setMe(next, false)
    void setCachedMe(next).catch(() => undefined)
  }
}

function startPolling(): void {
  if (pollTimer) return
  pollTimer = setInterval(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return
    void checkDeviceNow()
  }, DEVICE_POLL_MS)
}

export function stopDevicePolling(): void {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = null
}

export function isDevicePolling(): boolean {
  return pollTimer !== null
}

/** Reconcile the gate with the lifecycle. `notify` lets a transition to allowed resume start-up. */
function settle(notify: boolean): boolean {
  const allowed = deviceAllowed()
  if (allowed) {
    stopDevicePolling()
    const wasGated = gated
    gated = false
    if (wasGated && notify) handlers.onAllowed()
  } else {
    startPolling()
    const wasGated = gated
    gated = true
    if (!wasGated) handlers.onGated()
  }
  return allowed
}

async function localDeviceId(): Promise<string | null> {
  try {
    return (await getOrCreateDeviceKey()).deviceId
  } catch (error) {
    console.warn('Device key unavailable', error)
    return null
  }
}

async function attestAndApply(options: AttestOptions = {}): Promise<void> {
  const me = useSession.getState().me
  if (!me) return
  const gate = useDeviceGate.getState()
  if (gate.policy === 'off') return
  const localId = await localDeviceId()
  const needsAttestation = Boolean(options.force) || me.device?.status === 'unbound' || me.device?.id !== localId
  if (needsAttestation) gate.setAttesting(true)
  try {
    applyDevice(await ensureDeviceAttested(me, options))
  } catch (error) {
    if (!isNetworkError(error)) console.warn('Device attestation failed', error)
    // An approval that belongs to another key must not open the app on this one.
    if (needsAttestation && me.device?.status === 'approved') applyDevice({ ...me.device, status: 'unbound' })
    useDeviceGate.setState({ error: errorMessage(error, "Couldn't register this device") })
  } finally {
    useDeviceGate.getState().setAttesting(false)
  }
}

/**
 * After GET /api/me succeeded: attest when needed. Returns true when the app may connect the hub
 * and sync; otherwise the gate is shown and polling has started.
 */
export async function runDeviceCheck(): Promise<boolean> {
  if (!useSession.getState().me) return false
  await attestAndApply()
  return settle(false)
}

/** "Check again" and the poll: GET /api/devices/me, re-attesting when the session lost its binding. */
export function checkDeviceNow(): Promise<void> {
  if (inFlight) return inFlight
  inFlight = (async () => {
    const gate = useDeviceGate.getState()
    if (gate.policy === 'off' || !useSession.getState().me) return
    gate.setChecking(true)
    let error: string | null = null
    try {
      let device = await api.deviceMe()
      const localId = await localDeviceId()
      const me = useSession.getState().me
      if (me && (device.status === 'unbound' || device.id !== localId)) {
        device = await ensureDeviceAttested({ ...me, device })
      }
      applyDevice(device)
    } catch (e) {
      error = errorMessage(e, "Couldn't check this device")
    } finally {
      useDeviceGate.getState().setChecking(false, error)
    }
    settle(true)
  })().finally(() => {
    inFlight = null
  })
  return inFlight
}

/** "Request again" for an expired request: a new attestation re-opens it. */
export async function requestDeviceAgain(): Promise<void> {
  useDeviceGate.getState().setChecking(true)
  await attestAndApply({ force: true })
  useDeviceGate.getState().setChecking(false)
  settle(true)
}

/** The API answered 403 device_not_approved somewhere: gate now, then confirm with the server. */
export function onDeviceNotApproved(): void {
  settle(true)
  const now = Date.now()
  if (now - lastReportCheck < MIN_RECHECK_GAP_MS) return
  lastReportCheck = now
  void checkDeviceNow()
}

/** PersonalEvent for a device: gate immediately when this installation was revoked. */
export async function onDeviceEvent(type: string, eventDeviceId: string | undefined): Promise<void> {
  if (useDeviceGate.getState().policy === 'off' || !eventDeviceId) return
  const localId = await localDeviceId()
  if (eventDeviceId !== localId) return
  const statusByType: Record<string, DeviceStatus> = {
    'device.revoked': 'revoked',
    'device.denied': 'denied',
    'device.approved': 'approved',
  }
  const status = statusByType[type]
  if (status && status !== 'approved') {
    applyDevice({ ...useDeviceGate.getState().device, status })
    settle(true)
  }
  void checkDeviceNow()
}

export function onVisibleWhileGated(): void {
  if (gated) void checkDeviceNow()
}

export function isGated(): boolean {
  return gated
}

/** Sign-out / tests. */
export function resetDeviceGate(): void {
  stopDevicePolling()
  gated = false
  inFlight = null
  lastReportCheck = 0
  useDeviceGate.getState().reset()
}
